import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  Assignment as ProjectIcon,
  Chair as FurnitureIcon,
  LocalShipping as BookingIcon,
  Storage as StorageIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    projects: { total: 0, list: [] },
    furniture: {
      total: 0,
      keep: 0,
      store: 0,
      sell: 0,
      discard: 0,
      total_volume: 0,
      total_value: 0,
      total_rental_cost: 0,
      total_new_cost: 0,
    },
    storage: {
      total_bookings: 0,
      estimated_months: 0,
      estimated_space: 0,
      estimated_cost: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback to basic stats
      try {
        const [projectsRes, furnitureRes] = await Promise.all([
          axios.get(`${API_URL}/projects`),
          axios.get(`${API_URL}/furniture`),
        ]);

        setStats({
          projects: { total: projectsRes.data.projects?.length || 0, list: projectsRes.data.projects || [] },
          furniture: {
            total: furnitureRes.data.furniture?.length || 0,
            keep: 0,
            store: 0,
            sell: 0,
            discard: 0,
            total_volume: 0,
            total_value: 0,
            total_rental_cost: 0,
            total_new_cost: 0,
          },
          storage: {
            total_bookings: 0,
            estimated_months: 0,
            estimated_space: 0,
            estimated_cost: 0,
          },
        });
      } catch (fallbackError) {
        console.error('Fallback stats error:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const quickActions = [
    {
      title: 'Create Project',
      description: 'Start a new redevelopment project',
      icon: <ProjectIcon sx={{ fontSize: 40 }} />,
      onClick: () => navigate('/projects'),
      color: 'primary',
    },
    {
      title: 'Add Furniture',
      description: 'Upload and assess your furniture',
      icon: <FurnitureIcon sx={{ fontSize: 40 }} />,
      onClick: () => navigate('/furniture'),
      color: 'secondary',
    },
    {
      title: 'Book Services',
      description: 'Book packing, moving, or storage',
      icon: <BookingIcon sx={{ fontSize: 40 }} />,
      onClick: () => navigate('/bookings'),
      color: 'success',
    },
  ];

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Projects
                  </Typography>
                  <Typography variant="h4">{stats.projects.total}</Typography>
                </Box>
                <ProjectIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Furniture Items
                  </Typography>
                  <Typography variant="h4">{stats.furniture.total}</Typography>
                </Box>
                <FurnitureIcon color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Value
                  </Typography>
                  <Typography variant="h4">₹{stats.furniture.total_value.toLocaleString()}</Typography>
                </Box>
                <MoneyIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Storage Space
                  </Typography>
                  <Typography variant="h4">{stats.storage.estimated_space.toFixed(0)}</Typography>
                  <Typography variant="caption" color="textSecondary">cu ft</Typography>
                </Box>
                <StorageIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Furniture Breakdown */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Furniture Breakdown
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Items</strong></TableCell>
                    <TableCell>{stats.furniture.total}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>To Keep</strong></TableCell>
                    <TableCell>{stats.furniture.keep}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>To Store</strong></TableCell>
                    <TableCell>{stats.furniture.store}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>To Sell</strong></TableCell>
                    <TableCell>{stats.furniture.sell}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>To Discard</strong></TableCell>
                    <TableCell>{stats.furniture.discard}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} md={6}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Volume</strong></TableCell>
                    <TableCell>{stats.furniture.total_volume.toFixed(2)} cu ft</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Estimated Value</strong></TableCell>
                    <TableCell>₹{stats.furniture.total_value.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Rental Cost</strong></TableCell>
                    <TableCell>₹{stats.furniture.total_rental_cost.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total New Cost</strong></TableCell>
                    <TableCell>₹{stats.furniture.total_new_cost.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Storage Planning */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Storage Planning
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Storage Space Required</strong></TableCell>
                    <TableCell>{stats.storage.estimated_space.toFixed(2)} cu ft</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Estimated Storage Months</strong></TableCell>
                    <TableCell>{stats.storage.estimated_months} months</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Estimated Storage Cost</strong></TableCell>
                    <TableCell>₹{stats.storage.estimated_cost.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Active Storage Bookings</strong></TableCell>
                    <TableCell>{stats.storage.total_bookings}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Cost Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Based on your furniture decisions, you'll need approximately{' '}
                  <strong>{stats.storage.estimated_space.toFixed(0)} cubic feet</strong> of storage space
                  for about <strong>{stats.storage.estimated_months} months</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated storage cost: <strong>₹{stats.storage.estimated_cost.toLocaleString()}</strong>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={action.onClick}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ color: `${action.color}.main`, mb: 2 }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
                <Button
                  variant="contained"
                  color={action.color}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Dashboard;
